/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-useless-escape */
import { cloneElement, useEffect } from "react";
import Cookies from "js-cookie";

import { Box, Card, Grid, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import WorkOffOutlinedIcon from "@mui/icons-material/WorkOffOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import WorkHistoryOutlinedIcon from "@mui/icons-material/WorkHistoryOutlined";
import Person4OutlinedIcon from "@mui/icons-material/Person4Outlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import DomainDisabledOutlinedIcon from "@mui/icons-material/DomainDisabledOutlined";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import RestorePageOutlinedIcon from "@mui/icons-material/RestorePageOutlined";
import { Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Table from "../../../../components/shared/Table";
import { getDashboardData, getStats } from "../../../../redux/dashboard/actions";
import PhoneNumberWithMenu from "../../../../components/shared/PhoneNumberWithMenu";
import { JobCategories } from "../../../../utilities/constants";
import { getAppliedJobs, getNewJobs, getNewUsers, getUpdatedUser } from "../../../../redux/dashboard/selectors";

function formatToMMDDYYYY(dateString) {
  if (!dateString) return "";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";

  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();

  return `${mm}/${dd}/${yyyy}`;
}

const LinkComponent = ({ text, link, id, tab }) => (
  <Link
    to={tab ? `/${link}/${id}?tab=${tab}` : `/${link}/${id}`}
    style={{
      color: "inherit",
      textDecoration: "none",
      transition: "color 0.2s ease",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}
    onMouseEnter={(e) => {
      e.target.style.color = "#d32f2f";
    }}
    onMouseLeave={(e) => {
      e.target.style.color = "inherit";
    }}
    onClick={(e) => {
      e.stopPropagation();
      Cookies.set("isFromDashboard", "true", { expires: 1 });
    }}>
    {text}
  </Link>
);

LinkComponent.propTypes = {
  text: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  tab: PropTypes.string.isRequired,
};

const userColumns = [
  {
    id: "id",
    label: "ID",
    props: {
      align: "center",
    },
  },
  {
    id: "email",
    label: "E-mail",
    value: (row) => <LinkComponent text={row?.email} link={row?.role === "PROMOTER" ? "promoters" : row?.role === "COMPANY" ? "companies" : "users"} id={row?.userId} tab="" />,
    props: {
      align: "left",
    },
  },
  {
    id: "mobile",
    label: "Mobile Number",
    value: (row) => <PhoneNumberWithMenu phone={row?.phone} />,
    props: {
      align: "center",
    },
  },
  {
    id: "fullName",
    label: "Name",
    value: (row) => <LinkComponent text={row?.fullName} link="users" id={row?.context?.userId} tab="" />,
    props: {
      align: "left",
    },
  },
  {
    id: "createdDate",
    label: "Created Date",
    value: (row) => formatToMMDDYYYY(row?.createdDate),
    props: {
      align: "left",
    },
  },
];

const apliedJobColumns = [
  {
    id: "id",
    label: "ID",
    props: {
      align: "center",
    },
  },
  {
    id: "email",
    label: "E-mail",
    value: (row) => row?.email,
    props: {
      align: "left",
    },
  },
  {
    id: "jobTitle",
    label: "Job Title",
    value: (row) => <LinkComponent text={row?.jobTitle} link="jobs" id={row?.jobId} tab="applicants" />,
    props: {
      align: "left",
    },
  },
  {
    id: "fullName",
    label: "Name",
    value: (row) => row?.fullName,
    props: {
      align: "left",
    },
  },
  {
    id: "createdDate",
    label: "Created Date",
    value: (row) => formatToMMDDYYYY(row?.createdDate),
    props: {
      align: "left",
    },
  },
];

const jobColumns = [
  {
    id: "id",
    label: "ID",
    props: {
      align: "center",
    },
  },
  {
    id: "jobTitle",
    label: "Job Title",
    value: (row) => <LinkComponent text={row?.jobTitle} link="jobs" id={row?.jobId} tab="" />,
    props: {
      align: "left",
    },
  },
  {
    id: "jobCategories",
    label: "Job Categories",
    value: (row) => {
      if (!row?.jobCategories) return "";

      let categories = [];

      if (Array.isArray(row.jobCategories)) {
        categories = row.jobCategories;
      } else if (typeof row.jobCategories === "string") {
        categories = row.jobCategories
          .replace(/[\[\]]/g, "")
          .split(",")
          .map((cat) => cat.trim());
      }

      const labels = categories.map((cat) => JobCategories[cat]?.label || cat);

      return <LinkComponent text={labels.join(", ")} link="jobs" id={row?.jobId} tab="" />;
    },
    props: {
      align: "left",
    },
  },
  {
    id: "companyName",
    label: "Company Name",
    value: (row) => row?.companyName,
    props: {
      align: "left",
    },
  },
  {
    id: "createdDate",
    label: "Created Date",
    value: (row) => formatToMMDDYYYY(row?.createdDate),
    props: {
      align: "left",
    },
  },
];

const updatedUserColumns = [
  {
    id: "id",
    label: "ID",
    props: {
      align: "center",
    },
  },
  {
    id: "email",
    label: "E-mail",
    value: (row) => row?.email,
    props: {
      align: "left",
    },
  },
  {
    id: "updateMessage",
    label: "Update Message",
    value: (row) => row?.updateMessage,
    props: {
      align: "left",
    },
  },
  // {
  //   id: "updatedContents",
  //   label: "Updated Contents",
  //   value: (row) => {
  //     const contents = row?.updatedContents;

  //     if (!contents || typeof contents !== "object") return "";

  //     return Object.entries(contents)
  //       .map(([key, value]) => `${key}: ${value}`)
  //       .join(", ");
  //   },
  //   props: {
  //     align: "left",
  //   },
  // },
  {
    id: "fullName",
    label: "Name",
    value: (row) => row?.fullName,
    props: {
      align: "left",
    },
  },
  {
    id: "updatedDate",
    label: "Updated Date",
    value: (row) => formatToMMDDYYYY(row?.updatedDate),
    props: {
      align: "left",
    },
  },
];

export const dashboardCardsConfig = {
  totalUsers: {
    label: "Total Users",
    color: "#2196F3",
    icon: <Person4OutlinedIcon />,
    moveTo: "/users",
    applyFilter: "{}",
  },
  activeUsers: {
    label: "Active Users",
    color: "#ABE969",
    icon: <TaskOutlinedIcon />,
    moveTo: "/users",
    applyFilter: '{"isEmailVerified": true}',
  },
  inActiveUsers: {
    label: "Inactive Users",
    color: "#D91919",
    icon: <RestorePageOutlinedIcon />,
    moveTo: "/users",
    applyFilter: '{"isEmailVerified": false}',
  },
  verifiedUsers: {
    label: "Verified Users",
    color: "#00BCD4",
    icon: <VerifiedOutlinedIcon />,
    moveTo: "/users",
    applyFilter: '{"isAccountVerified": true}',
  },
  totalJobs: {
    label: "Total Jobs",
    color: "#3F51B5",
    icon: <WorkOutlineOutlinedIcon />,
    moveTo: "/jobs",
    applyFilter: "{}",
  },
  activeJobs: {
    label: "Active Jobs",
    color: "#4CAF50",
    icon: <WorkHistoryOutlinedIcon />,
    moveTo: "/jobs",
    applyFilter: '{"status":"ACTIVE"}',
  },
  inactiveJobs: {
    label: "Inactive Jobs",
    color: "#9C27B0",
    icon: <WorkOffOutlinedIcon />,
    moveTo: "/jobs",
    applyFilter: '{"status":"INACTIVE"}',
  },
  totalUsersApplied: {
    label: "Users Applied",
    color: "#19c969",
    icon: <HowToRegOutlinedIcon />,
    moveTo: "",
    applyFilter: "",
  },
  totalPromoters: {
    label: "Total Promoters",
    color: "#795548",
    icon: <GroupsOutlinedIcon />,
    moveTo: "/promoters",
    applyFilter: "",
  },
  totalCompanies: {
    label: "Total Companies",
    color: "#4472C4",
    icon: <BusinessOutlinedIcon />,
    moveTo: "/companies",
    applyFilter: "",
  },
  verifiedCompanies: {
    label: "Verified Companies",
    color: "#009688",
    icon: <VerifiedOutlinedIcon />,
    moveTo: "/companies",
    applyFilter: '{"isVerified": true}',
  },
  unVerifiedCompanies: {
    label: "Unverified Companies",
    color: "#FF5722",
    icon: <DomainDisabledOutlinedIcon />,
    moveTo: "/companies",
    applyFilter: '{"isVerified": false}',
  },
};

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoading = useSelector((state) => state.dashboard.loading);
  const newUsers = useSelector(getNewUsers);
  const newAppliedJobs = useSelector(getAppliedJobs);
  const newJobs = useSelector(getNewJobs);
  const updatedUser = useSelector(getUpdatedUser);
  const stats = useSelector((state) => state.dashboard.stats || []);

  useEffect(() => {
    dispatch(getDashboardData());
    dispatch(getStats());
  }, []);

  const handleCardClick = (config) => {
    if (!config.moveTo) return; // ignore cards without moveTo

    // Store applyFilter only if not empty
    if (config.applyFilter && config.applyFilter !== "") {
      Cookies.set("applyFilter", config.applyFilter, { expires: 1 });
    }

    navigate(config.moveTo);
  };

  return (
    <Box>
      {Object.keys(dashboardCardsConfig).length > 0 &&
        [...Array(Math.ceil(Object.keys(dashboardCardsConfig).length / 4))].map((key, rowIndex) => (
          <Grid
            container
            key={`${key}_${rowIndex.toString()}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}>
            {Object.keys(dashboardCardsConfig)
              .slice(rowIndex * 4, rowIndex * 4 + 4)
              .map((itemKey) => {
                const config = dashboardCardsConfig[itemKey];
                const value = stats[itemKey];

                if (value === undefined) return null;

                const isClickable = config.moveTo && config.moveTo.trim() !== "";

                return (
                  <Grid
                    item
                    key={`${itemKey}_${rowIndex.toString()}`}
                    xs={12}
                    sm={5.88}
                    lg={2.88}
                    xl={2.9}
                    style={{
                      marginBottom: "10px",
                    }}>
                    <Card
                      style={{
                        width: "100%",
                        border: `1px solid ${config.color}`,
                        boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                        transition: "0.3s ease",
                        cursor: isClickable ? "pointer" : "default",
                      }}
                      onClick={() => isClickable && handleCardClick(config)}>
                      <div
                        style={{
                          display: "flex",
                          padding: "4px 20px",
                          alignItems: "center",
                        }}>
                        <span
                          style={{
                            color: config.color,
                            borderRight: "1px solid #D3D3D3",
                            paddingRight: "12px",
                            display: "flex",
                            alignItems: "center",
                          }}>
                          {cloneElement(config.icon, { style: { fontSize: 22 } })}
                        </span>

                        <div
                          style={{
                            display: "inline-flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            paddingLeft: "12px",
                          }}>
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#333333",
                            }}>
                            {value}
                          </span>

                          <span
                            style={{
                              fontSize: "14px",
                              color: "#708090",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}>
                            {config.label}
                          </span>
                        </div>
                      </div>
                    </Card>
                  </Grid>
                );
              })}
          </Grid>
        ))}

      <Grid
        container
        style={{
          paddingTop: "20px",
        }}>
        <Grid item sm={12} md={6} lg={6} style={{ marginBottom: "20px" }}>
          <Box>
            <Box mb={3}>
              <Typography variant="h5">Lists of New Users</Typography>
            </Box>
            <Table
              columns={userColumns}
              data={newUsers}
              isAlign
              isLoading={isLoading}
              options={{
                shortenContent: false,
              }}
            />
          </Box>
        </Grid>
        <Grid item sm={12} md={6} lg={6} style={{ marginBottom: "20px" }}>
          <Box>
            <Box mb={3}>
              <Typography variant="h5">New Applied to Jobs</Typography>
            </Box>
            <Table
              columns={apliedJobColumns}
              data={newAppliedJobs}
              isAlign
              isLoading={isLoading}
              options={{
                shortenContent: false,
              }}
            />
          </Box>
        </Grid>
        <Grid item sm={12} md={6} lg={6}>
          <Box>
            <Box mb={3}>
              <Typography variant="h5">New Jobs Created</Typography>
            </Box>
            <Table
              columns={jobColumns}
              data={newJobs}
              isAlign
              isLoading={isLoading}
              options={{
                shortenContent: false,
              }}
            />
          </Box>
        </Grid>
        <Grid item sm={12} md={6} lg={6}>
          <Box>
            <Box mb={3}>
              <Typography variant="h5">Newly Updated Users</Typography>
            </Box>
            <Table
              columns={updatedUserColumns}
              data={updatedUser}
              isAlign
              isLoading={isLoading}
              options={{
                shortenContent: false,
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
